import { z } from 'zod';

import {
  timeEntryAdjustmentStatusValues,
  type ApproveTimeEntryAdjustmentRequest,
  type ApproveTimeEntryAdjustmentResponse,
  type CreateTimeEntryAdjustmentRequest,
  type CreateTimeEntryAdjustmentResponse,
  type ListMyTimeEntryAdjustmentsRequest,
  type ListMyTimeEntryAdjustmentsResponse,
  type ListTimeEntryAdjustmentsRequest,
  type ListTimeEntryAdjustmentsResponse,
  type RejectTimeEntryAdjustmentRequest,
  type RejectTimeEntryAdjustmentResponse,
  type RequestTimeEntryAdjustmentRequest,
  type RequestTimeEntryAdjustmentResponse,
  type TimeEntryAdjustmentDto,
  type TimeEntryAdjustmentStatus,
} from './time-entry-adjustments.dto.js';
import { TimeEntryDtoSchema } from './time-tracking.schema.js';

const timestampSchema = z.iso.datetime();

export const TimeEntryAdjustmentStatusSchema: z.ZodType<TimeEntryAdjustmentStatus> = z.enum(
  timeEntryAdjustmentStatusValues,
);

export const TimeEntryAdjustmentDtoSchema: z.ZodType<TimeEntryAdjustmentDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  employeeId: z.uuid(),
  timeEntryId: z.uuid().nullable(),
  requestedByUserId: z.uuid(),
  requestedAt: timestampSchema,
  status: TimeEntryAdjustmentStatusSchema,
  reason: z.string().trim().min(1).max(500),
  resolutionReason: z.string().trim().min(1).max(500).nullable(),
  originalClockInAt: timestampSchema.nullable(),
  originalClockOutAt: timestampSchema.nullable(),
  newClockInAt: timestampSchema,
  newClockOutAt: timestampSchema.nullable(),
  approvedByUserId: z.uuid().nullable(),
  approvedAt: timestampSchema.nullable(),
  rejectedByUserId: z.uuid().nullable(),
  rejectedAt: timestampSchema.nullable(),
  affectsClosedPayPeriod: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const RequestTimeEntryAdjustmentRequestSchema: z.ZodType<RequestTimeEntryAdjustmentRequest> = z.object({
  companyId: z.uuid(),
  timeEntryId: z.uuid().nullable().optional(),
  newClockInAt: timestampSchema,
  newClockOutAt: timestampSchema.nullable(),
  reason: z.string().trim().min(1).max(500),
});

export const RequestTimeEntryAdjustmentResponseSchema: z.ZodType<RequestTimeEntryAdjustmentResponse> = z.object({
  adjustment: TimeEntryAdjustmentDtoSchema,
});

export const CreateTimeEntryAdjustmentRequestSchema: z.ZodType<CreateTimeEntryAdjustmentRequest> = z.object({
  companyId: z.uuid(),
  employeeId: z.uuid(),
  timeEntryId: z.uuid().nullable().optional(),
  newClockInAt: timestampSchema,
  newClockOutAt: timestampSchema.nullable(),
  reason: z.string().trim().min(1).max(500),
});

export const CreateTimeEntryAdjustmentResponseSchema: z.ZodType<CreateTimeEntryAdjustmentResponse> = z.object({
  adjustment: TimeEntryAdjustmentDtoSchema,
  timeEntry: TimeEntryDtoSchema,
});

export const ListTimeEntryAdjustmentsRequestSchema: z.ZodType<ListTimeEntryAdjustmentsRequest> = z.object({
  companyId: z.uuid(),
  status: TimeEntryAdjustmentStatusSchema.optional(),
  employeeId: z.uuid().optional(),
});

export const ListTimeEntryAdjustmentsResponseSchema: z.ZodType<ListTimeEntryAdjustmentsResponse> = z.object({
  items: z.array(TimeEntryAdjustmentDtoSchema),
});

export const ListMyTimeEntryAdjustmentsRequestSchema: z.ZodType<ListMyTimeEntryAdjustmentsRequest> = z.object({
  companyId: z.uuid(),
});

export const ListMyTimeEntryAdjustmentsResponseSchema: z.ZodType<ListMyTimeEntryAdjustmentsResponse> = z.object({
  items: z.array(TimeEntryAdjustmentDtoSchema),
});

export const ApproveTimeEntryAdjustmentRequestSchema: z.ZodType<ApproveTimeEntryAdjustmentRequest> = z.object({
  companyId: z.uuid(),
  adjustmentId: z.uuid(),
  resolutionReason: z.string().trim().min(1).max(500).nullable().optional(),
});

export const ApproveTimeEntryAdjustmentResponseSchema: z.ZodType<ApproveTimeEntryAdjustmentResponse> = z.object({
  adjustment: TimeEntryAdjustmentDtoSchema,
  timeEntry: TimeEntryDtoSchema,
});

export const RejectTimeEntryAdjustmentRequestSchema: z.ZodType<RejectTimeEntryAdjustmentRequest> = z.object({
  companyId: z.uuid(),
  adjustmentId: z.uuid(),
  resolutionReason: z.string().trim().min(1).max(500),
});

export const RejectTimeEntryAdjustmentResponseSchema: z.ZodType<RejectTimeEntryAdjustmentResponse> = z.object({
  adjustment: TimeEntryAdjustmentDtoSchema,
});
